/**
 * A chart a person or the memo chat put in a section (DOP-655), drawn as the
 * chat draws its own charts, with where its values came from under it.
 *
 * A chart is never the run's: it is added from the chat or carried from an
 * earlier version, so it reads in the person's colour like any addition, and
 * a document it names opens where the values were read.
 */

import React from "react";

import type { DealScreeningChart, DealScreeningSource } from "../../types/dealScreening";
import EChartsBlock from "../EChartsBlock";

export interface MemoChartProps {
	chart: DealScreeningChart;
	/** Stable id for the chart's canvas: "<section>:<paragraph>". */
	idPrefix: string;
	onOpenSource?: (source: DealScreeningSource) => void;
	/** Who put it in and when, for a source that is their own note. */
	noteBy?: string | null;
	compact?: boolean;
}

/** What a source reads as under the chart. */
export function chartSourceLabel(source: DealScreeningSource): string {
	if (source.kind === "user") return source.note?.trim() || "Entered by a person";
	if (source.sheet && source.cell) return `${source.filename} · ${source.sheet}!${source.cell}`;
	if (source.page) return `${source.filename} · p. ${source.page}`;
	return source.filename;
}

const MemoChart: React.FC<MemoChartProps> = ({ chart, idPrefix, onOpenSource, noteBy = null, compact = false }) => {
	const sources = chart.sources ?? [];
	return (
		<figure className={compact ? "my-1" : "my-3"} data-testid="memo-chart">
			{/* In a narrow place (a card, edit mode) the title is a caption that
			    wraps; the chart's own title would be cut at the edges. */}
			{compact && chart.title && (
				<figcaption className="mb-1 font-sans text-[12px] font-semibold text-[#3f4247]">{chart.title}</figcaption>
			)}
			<div className="rounded-xl border border-[#e7e7e4] bg-white px-2 pt-2">
				<EChartsBlock
					chartData={chart.data}
					chartType={chart.chart_type}
					title={compact ? undefined : (chart.title ?? undefined)}
					version={1}
					chartId={idPrefix}
				/>
			</div>
			{sources.length > 0 && (
				<figcaption className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-sans text-xs text-[#6B7280]">
					<span>Source{sources.length > 1 ? "s" : ""}:</span>
					{sources.map((source, index) =>
						source.kind === "user" || !onOpenSource ? (
							<span
								// A chart's sources are positional; the chart is immutable.
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								className="text-[#5b3fa8]"
								title={noteBy ? `Entered by ${noteBy}` : undefined}
								data-testid="memo-chart-source"
							>
								{chartSourceLabel(source)}
							</span>
						) : (
							<button
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								type="button"
								className="text-[#5b3fa8] underline decoration-dotted underline-offset-2 hover:text-[#3f2a7a]"
								onClick={() => onOpenSource(source)}
								data-testid="memo-chart-source"
							>
								{chartSourceLabel(source)}
							</button>
						),
					)}
				</figcaption>
			)}
		</figure>
	);
};

export default React.memo(MemoChart);
